import { useEffect, useMemo, useState } from 'react';
import { useKioskLandingConfig } from '../../face-identity/hooks/useKioskLandingConfig';
import { useOptionalSkinSession } from '../context/SkinSessionContext';
import {
  KIOSK_PAGE_KEYS,
  buildBackgroundStyle,
} from '../../face-identity/utils/kioskLandingDefaults';
import {
  SESSION_APP_DATA_BASE,
  SESSION_MASTER_TOKEN_URL,
} from '../config/sessionAppData';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { franchiseAuthHeaders, appendFranchiseThemeParams } from '../../shared/franchiseFlowAuth';
import { applyPageSetupResult, storePageSetupData } from './PageBackgroundSync';
import './SalonLandingBackground.css';

function hexToRgb(hex) {
  const raw = String(hex || '').replace('#', '').trim();
  if (raw.length !== 3 && raw.length !== 6) return null;
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;
  const n = Number.parseInt(full, 16);
  if (!Number.isFinite(n)) return null;
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

/** Convert page-setup background shape into kiosk landing background fields. */
function pageSetupToLandingBg(pageSetupBg) {
  if (!pageSetupBg || typeof pageSetupBg !== 'object') return null;
  const primary = pageSetupBg.primary_color || pageSetupBg.primaryColor;
  const secondary = pageSetupBg.secondary_color || pageSetupBg.secondaryColor;
  if (!primary && !secondary) return null;
  const type = String(pageSetupBg.type || 'gradient').toLowerCase();
  const from = primary || secondary;
  const to = type === 'gradient' ? secondary || primary : from;
  const angle =
    type === 'gradient'
      ? Number.isFinite(Number(pageSetupBg.gradient_angle))
        ? Number(pageSetupBg.gradient_angle)
        : 90
      : 0;
  return {
    gradientFrom: from,
    gradientTo: to,
    gradientAngle: angle,
  };
}

async function resolvePageSetupPayload(tokenHint) {
  let token = tokenHint || null;
  if (!token) {
    try {
      const res = await fetchWithRetry(`${SESSION_MASTER_TOKEN_URL}?_=${Date.now()}`, {
        timeoutMs: 12000,
        cache: 'no-store',
        credentials: 'include',
        headers: franchiseAuthHeaders(),
      }, { label: 'landing-master-token' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) token = data?.public_token || null;
    } catch {
      return null;
    }
  }
  if (!token) return null;
  try {
    const qs = appendFranchiseThemeParams({ _: String(Date.now()) });
    const res = await fetchWithRetry(
      `${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/page-setup?${qs.toString()}`,
      {
        timeoutMs: 15000,
        cache: 'no-store',
        credentials: 'include',
        headers: franchiseAuthHeaders(),
      },
      { label: 'landing-page-setup' },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return null;
    return { token, result: data };
  } catch {
    return null;
  }
}

/**
 * Fixed background layer for Naturals salon shells.
 * @param {'kiosk'|'pageSetup'} source
 *   - kiosk: Kiosk Landings only (legacy)
 *   - pageSetup: Workspace Theme & page setup (all kiosk + salon pages)
 */
export default function SalonLandingBackground({
  pageKey = KIOSK_PAGE_KEYS.SALON_ENTRY,
  source = 'pageSetup',
  sessionToken: sessionTokenProp = null,
}) {
  const session = useOptionalSkinSession();
  const token = sessionTokenProp || session?.token || null;
  const { config } = useKioskLandingConfig(pageKey);
  const [pageSetupBg, setPageSetupBg] = useState(null);
  const usePageSetupOnly = source === 'pageSetup';

  useEffect(() => {
    if (!usePageSetupOnly) {
      setPageSetupBg(null);
      return undefined;
    }
    let cancelled = false;
    resolvePageSetupPayload(token).then((payload) => {
      if (cancelled || !payload?.result) return;
      applyPageSetupResult(payload.result);
      storePageSetupData(payload.token, payload.result);
      const fields =
        payload.result?.external_fields ||
        payload.result?.data?.external_fields ||
        null;
      if (fields?.background) setPageSetupBg(fields.background);
    });
    return () => {
      cancelled = true;
    };
  }, [token, usePageSetupOnly]);

  const background = useMemo(() => {
    if (usePageSetupOnly) {
      return pageSetupToLandingBg(pageSetupBg) || {
        gradientFrom: '#fffdf9',
        gradientTo: '#f3e8dc',
        gradientAngle: 180,
      };
    }
    return config?.background && typeof config.background === 'object'
      ? { ...config.background }
      : {};
  }, [usePageSetupOnly, pageSetupBg, config?.background]);

  const style = useMemo(
    () => buildBackgroundStyle(background),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- primitive fields drive the paint
    [
      background.gradientFrom,
      background.gradientTo,
      background.gradientAngle,
      background.gradientBlendStart,
      background.backdropImageUrl,
      background.backdropOpacity,
      background.backdropBlendMode,
    ],
  );
  const hasBackdrop = Boolean(!usePageSetupOnly && background.backdropImageUrl);

  useEffect(() => {
    const roots = [document.documentElement];
    try {
      document.querySelectorAll('.naturals-salon-lux, .fi-lux-theme').forEach((el) => roots.push(el));
    } catch {
      /* ignore */
    }
    const gradient = style.backgroundImage || style.background || '';
    const from = background.gradientFrom || '#faf4eb';
    const to = background.gradientTo || '#f0d9a8';
    roots.forEach((root) => {
      if (gradient) {
        root.style.setProperty('--page-bg-background', String(gradient));
        root.style.setProperty('--kiosk-page-bg', String(gradient));
      }
      root.style.setProperty('--page-bg-primary', from);
      root.style.setProperty('--page-bg-secondary', to);
      const fromRgb = hexToRgb(from);
      const toRgb = hexToRgb(to);
      if (fromRgb) root.style.setProperty('--page-bg-primary-rgb', fromRgb);
      if (toRgb) root.style.setProperty('--page-bg-secondary-rgb', toRgb);
    });
  }, [style, background.gradientFrom, background.gradientTo]);

  return (
    <div
      className={`salon-landing-bg${hasBackdrop ? ' salon-landing-bg--backdrop' : ''}`}
      style={style}
      aria-hidden="true"
    />
  );
}

export { KIOSK_PAGE_KEYS };
